import { useEffect, useState } from "react"
import { CalendarClock } from "lucide-react"
import { Link } from "@inertiajs/react"

import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { formatMinutes } from "@/features/schedule/schedule-time"
import {
  formatDuration,
  minutesUntil,
  tTill,
  type JourneyStatus,
  type ShiftJourney,
} from "@/features/staff/till"
import { formatDateTime } from "@/lib/formatters"
import { toneDotClass, type StatusTone } from "@/lib/status-colors"

/**
 * Which day the till is covering.
 *
 * The strip exists because the drawer alone cannot answer the two questions a
 * shift is planned around — is the restaurant supposed to be open right now,
 * and for how much longer. A cashier who knows the kitchen closes in forty
 * minutes takes a different last order than one who does not.
 *
 * **Nothing here is derived from the browser's clock except the countdown.**
 * The status, the periods and both boundaries arrive answered from
 * `Staff::LocationJourneyQuery`, in the restaurant's own zone. The countdown is
 * a difference between two instants, which is the one calculation that means
 * the same thing in every zone.
 *
 * And it ticks. A till is a screen someone leaves open on a counter for the
 * length of a service: computed once at render, "cierra en 6 h 30 min" is still
 * on the tablet at eleven at night, which is the till stating something false on
 * the one line added to answer how much longer. When the instant passes, the
 * badge falls back to the status the server sent rather than counting into
 * negatives -- the next visit will re-answer it.
 *
 * Periods are the ones that START today, so an overnight Friday night does not
 * reappear on Saturday's line. What Saturday shows instead is the countdown to
 * the close it is still inside — which is the fact the person standing there at
 * half past midnight actually needs.
 */

const STATUS_TONE: Record<JourneyStatus, StatusTone> = {
  open: "available",
  opens_later: "occupied",
  closed_today: "disabled",
  not_configured: "disabled",
  disabled: "disabled",
}

/** A clock that re-renders once a minute, which is the resolution shown. */
function useMinuteClock() {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  return now
}

function countdown(journey: ShiftJourney, now: number) {
  if (journey.status === "open") {
    const minutes = minutesUntil(journey.closesAt, now)
    return minutes === null ? null : tTill("journey_closes_in", { duration: formatDuration(minutes) })
  }

  if (journey.status === "opens_later") {
    const minutes = minutesUntil(journey.opensAt, now)
    return minutes === null ? null : tTill("journey_opens_in", { duration: formatDuration(minutes) })
  }

  return null
}

function hint(journey: ShiftJourney) {
  if (journey.status === "not_configured") return tTill("journey_not_configured_hint")
  if (journey.status === "disabled") return tTill("journey_disabled_hint")

  return tTill("journey_zone_hint")
}

export function TillJourneyStrip({ journey }: { journey: ShiftJourney }) {
  const now = useMinuteClock()
  const relative = countdown(journey, now)

  return (
    <Card density="compact" data-testid="shift-journey">
      <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <CalendarClock className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0 space-y-1">
            {/* `date` is already the restaurant's local date, so it is
                formatted in UTC: running a date-only string through the
                reader's zone is how "viernes 22" renders as "jueves 21" for
                anyone west of the restaurant. */}
            <p className="text-sm font-semibold first-letter:uppercase">
              {formatDateTime(journey.date, {
                options: { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" },
              })}
            </p>
            <p className="text-xs text-muted-foreground">
              {tTill("journey_hours")}
              {" · "}
              {hint(journey)}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {journey.periods.map((period) => (
            <Badge
              key={`${period.startsAt}-${period.endsAt}`}
              variant="outline"
              className="tabular-nums"
              data-testid={`shift-journey-period-${period.startsAt}`}
            >
              {formatMinutes(period.startsAt)} – {formatMinutes(period.endsAt)}
            </Badge>
          ))}

          <Badge variant="outline" className="gap-1.5" data-testid="shift-journey-status">
            <span aria-hidden="true" className={toneDotClass(STATUS_TONE[journey.status])} />
            {relative ?? tTill(`journey_status_${journey.status}`)}
          </Badge>

          {journey.status === "not_configured" ? (
            <Link
              href="/manage/business_hours"
              className="text-sm font-medium text-brand-ink"
              data-testid="shift-journey-configure"
            >
              {tTill("journey_configure")}
            </Link>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
